import React, { useState, useRef, useEffect } from "react";
import { FaCheck, FaChevronDown, FaPlug, FaInfinity } from "react-icons/fa";
import { useTranslation } from "react-i18next";

interface BusinessModelOption {
  value: "plugin-based" | "pay-as-you-go";
  label: string;
  description: string;
  icon: React.ReactNode;
}

interface BusinessModelSelectorProps {
  value: "plugin-based" | "pay-as-you-go" | "";
  onChange: (value: "plugin-based" | "pay-as-you-go") => void;
  className?: string;
}

const businessModelOptions: BusinessModelOption[] = [
  {
    value: "pay-as-you-go",
    label: "Pay-As-You-Go",
    description: "platform.onboarding.payAsYouGoDescription",
    icon: <FaInfinity className="text-blue-600" size={24} />,
  },
  {
    value: "plugin-based",
    label: "Plugin-Based",
    description: "platform.onboarding.pluginBasedDescription",
    icon: <FaPlug className="text-blue-600" size={24} />,
  },
];

export const BusinessModelSelector: React.FC<BusinessModelSelectorProps> = ({
  value,
  onChange,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedOption =
    businessModelOptions.find((opt) => opt.value === value) ||
    businessModelOptions[0];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between hover:border-blue-400 transition-colors"
      >
        <div className="flex items-center gap-3 flex-1">
          {selectedOption.icon}
          <div className="flex-1">
            <div className="font-semibold text-gray-900">
              {selectedOption.label}
            </div>
            <div className="text-sm text-gray-500 mt-0.5">
              {t(selectedOption.description)}
            </div>
          </div>
        </div>
        <FaChevronDown
          className={`ml-2 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200  overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            {businessModelOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-4 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                  value === option.value ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">{option.icon}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">
                        {option.label}
                      </span>
                      {value === option.value && (
                        <FaCheck className="text-blue-600 text-sm" />
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {t(option.description)}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
